/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { BusinessCategory } from '../models/business-category';
import { BusinessType } from '../models/business-type';
import { CreateInvoiceRequest } from '../models/create-invoice-request';
import { CreateSalesOrderInvoiceRequest } from '../models/create-sales-order-invoice-request';
import { CreateWorkOrderInvoiceRequest } from '../models/create-work-order-invoice-request';
import { CreditNoteRequest } from '../models/credit-note-request';
import { CreditNoteResponse } from '../models/credit-note-response';
import { CreditNoteType } from '../models/credit-note-type';
import { DebitNoteRequest } from '../models/debit-note-request';
import { GetInvoiceResponse } from '../models/get-invoice-response';
import { GetZatcaInvoiceResponse } from '../models/get-zatca-invoice-response';
import { InvoiceConfirmRequest } from '../models/invoice-confirm-request';
import { InvoiceEbsUpdateRequest } from '../models/invoice-ebs-update-request';
import { InvoiceResponse } from '../models/invoice-response';
import { InvoiceUpdateRequest } from '../models/invoice-update-request';
import { RecordOrigin } from '../models/record-origin';
import { TrainingType } from '../models/training-type';
import { UpdateInvoiceRequest } from '../models/update-invoice-request';
import { WorkOrderInvoiceResponse } from '../models/work-order-invoice-response';
import { ZatcaStatus } from '../models/zatca-status';

/**
 * Invoice
 */
@Injectable({ providedIn: 'root' })
export class InvoiceService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllInvoices()` */
  static readonly GetAllInvoicesPath = '/api/v1/invoices';

  /**
   * Get list of invoices.
   *
   * Lists all invoices as a page with filters applied.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllInvoices()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllInvoices$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields:
       *   - invoiceNumber
       */
      search?: string;

      /**
       * this adds a string search to the following fields:
       *   - client
       */
      clientId?: number;

      /**
       * This adds a filter for the invoice:
       *       - ebs failed
       */
      isEbsFailed?: boolean;

      /**
       * This adds a filter for the invoice:
       *   - draft
       */
      draft?: boolean;

      /**
       * This allows filtering invoices by their business type
       */
      businessType?: Array<BusinessType>;

      /**
       * This adds a filter for the Training invoices
       */
      trainingType?: TrainingType;

      /**
       * This adds a filtering invoice by service order
       */
      serviceOrderId?: number;

      /**
       * This adds a filtering invoice by parent id
       */
      parentId?: number;

      /**
       * This adds a filtering invoice by invoice type
       */
      invoiceType?: string;

      /**
       * This adds a filtering invoice by sales order
       */
      salesOrderId?: number;

      /**
       * This adds a filter for the credit note invoices
       */
      creditNoteType?: CreditNoteType;

      /**
       * When set, limits the list to that origin. Omitted returns both.
       * INTERNAL includes records with no stored origin.
       */
      origin?: RecordOrigin;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetInvoiceResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      InvoiceService.GetAllInvoicesPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
      rb.query('clientId', params.clientId, {});
      rb.query('isEbsFailed', params.isEbsFailed, {});
      rb.query('draft', params.draft, {});
      rb.query('businessType', params.businessType, {});
      rb.query('trainingType', params.trainingType, {});
      rb.query('serviceOrderId', params.serviceOrderId, {});
      rb.query('parentId', params.parentId, {});
      rb.query('invoiceType', params.invoiceType, {});
      rb.query('salesOrderId', params.salesOrderId, {});
      rb.query('creditNoteType', params.creditNoteType, {});
      rb.query('origin', params.origin, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetInvoiceResponse>;
        })
      );
  }

  /**
   * Get list of invoices.
   *
   * Lists all invoices as a page with filters applied.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllInvoices$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllInvoices(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields:
       *   - invoiceNumber
       */
      search?: string;

      /**
       * this adds a string search to the following fields:
       *   - client
       */
      clientId?: number;

      /**
       * This adds a filter for the invoice:
       *       - ebs failed
       */
      isEbsFailed?: boolean;

      /**
       * This adds a filter for the invoice:
       *   - draft
       */
      draft?: boolean;

      /**
       * This allows filtering invoices by their business type
       */
      businessType?: Array<BusinessType>;

      /**
       * This adds a filter for the Training invoices
       */
      trainingType?: TrainingType;

      /**
       * This adds a filtering invoice by service order
       */
      serviceOrderId?: number;

      /**
       * This adds a filtering invoice by parent id
       */
      parentId?: number;

      /**
       * This adds a filtering invoice by invoice type
       */
      invoiceType?: string;

      /**
       * This adds a filtering invoice by sales order
       */
      salesOrderId?: number;

      /**
       * This adds a filter for the credit note invoices
       */
      creditNoteType?: CreditNoteType;

      /**
       * When set, limits the list to that origin. Omitted returns both.
       * INTERNAL includes records with no stored origin.
       */
      origin?: RecordOrigin;
    },
    context?: HttpContext
  ): Observable<GetInvoiceResponse> {
    return this.getAllInvoices$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<GetInvoiceResponse>): GetInvoiceResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `createInvoice()` */
  static readonly CreateInvoicePath = '/api/v1/invoices';

  /**
   * Create new invoice.
   *
   * Create a new invoice.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createInvoice()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createInvoice$Response(
    params: {
      body: CreateInvoiceRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<InvoiceResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      InvoiceService.CreateInvoicePath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<InvoiceResponse>;
        })
      );
  }

  /**
   * Create new invoice.
   *
   * Create a new invoice.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createInvoice$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createInvoice(
    params: {
      body: CreateInvoiceRequest;
    },
    context?: HttpContext
  ): Observable<InvoiceResponse> {
    return this.createInvoice$Response(params, context).pipe(
      map((r: StrictHttpResponse<InvoiceResponse>): InvoiceResponse => r.body)
    );
  }

  /** Path part for operation `createWorkOrderInvoice()` */
  static readonly CreateWorkOrderInvoicePath = '/api/v1/invoices/work-orders';

  /**
   * Create invoice of work orders.
   *
   * Create a invoices of work orders.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createWorkOrderInvoice()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createWorkOrderInvoice$Response(
    params: {
      body: CreateWorkOrderInvoiceRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkOrderInvoiceResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      InvoiceService.CreateWorkOrderInvoicePath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<WorkOrderInvoiceResponse>;
        })
      );
  }

  /**
   * Create invoice of work orders.
   *
   * Create a invoices of work orders.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createWorkOrderInvoice$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createWorkOrderInvoice(
    params: {
      body: CreateWorkOrderInvoiceRequest;
    },
    context?: HttpContext
  ): Observable<WorkOrderInvoiceResponse> {
    return this.createWorkOrderInvoice$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<WorkOrderInvoiceResponse>
        ): WorkOrderInvoiceResponse => r.body
      )
    );
  }

  /** Path part for operation `createCreditNote()` */
  static readonly CreateCreditNotePath = '/api/v1/invoice/{id}/credit-note';

  /**
   * Create credit note against an invoice for GIS.
   *
   * Create a credit note for invoice
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createCreditNote()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createCreditNote$Response(
    params: {
      /**
       * The invoice's id that needs to be credited
       */
      id: number;
      body: CreditNoteRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<CreditNoteResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      InvoiceService.CreateCreditNotePath,
      'post'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<CreditNoteResponse>;
        })
      );
  }

  /**
   * Create credit note against an invoice for GIS.
   *
   * Create a credit note for invoice
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createCreditNote$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createCreditNote(
    params: {
      /**
       * The invoice's id that needs to be credited
       */
      id: number;
      body: CreditNoteRequest;
    },
    context?: HttpContext
  ): Observable<CreditNoteResponse> {
    return this.createCreditNote$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<CreditNoteResponse>): CreditNoteResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `createDebitNote()` */
  static readonly CreateDebitNotePath = '/api/v1/invoice/{id}/debit-note';

  /**
   * Create Debit note against an credit note for GIS.
   *
   * Create a Debit note for invoice
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createDebitNote()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createDebitNote$Response(
    params: {
      /**
       * The invoice's id that needs to be debited
       */
      id: number;
      body: DebitNoteRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<CreditNoteResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      InvoiceService.CreateDebitNotePath,
      'post'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<CreditNoteResponse>;
        })
      );
  }

  /**
   * Create Debit note against an credit note for GIS.
   *
   * Create a Debit note for invoice
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createDebitNote$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createDebitNote(
    params: {
      /**
       * The invoice's id that needs to be debited
       */
      id: number;
      body: DebitNoteRequest;
    },
    context?: HttpContext
  ): Observable<CreditNoteResponse> {
    return this.createDebitNote$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<CreditNoteResponse>): CreditNoteResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `getInvoiceById()` */
  static readonly GetInvoiceByIdPath = '/api/v1/invoices/{id}';

  /**
   * fetch Invoice details.
   *
   * Returns details regarding an invoice.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getInvoiceById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getInvoiceById$Response(
    params: {
      /**
       * The invoice's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<InvoiceResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      InvoiceService.GetInvoiceByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<InvoiceResponse>;
        })
      );
  }

  /**
   * fetch Invoice details.
   *
   * Returns details regarding an invoice.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getInvoiceById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getInvoiceById(
    params: {
      /**
       * The invoice's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<InvoiceResponse> {
    return this.getInvoiceById$Response(params, context).pipe(
      map((r: StrictHttpResponse<InvoiceResponse>): InvoiceResponse => r.body)
    );
  }

  /** Path part for operation `confirmInvoice()` */
  static readonly ConfirmInvoicePath = '/api/v1/invoices/{id}';

  /**
   * confirm invoice.
   *
   * Confirms a drafted invoice.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `confirmInvoice()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  confirmInvoice$Response(
    params: {
      /**
       * The invoice's id that needs to be updated
       */
      id: number;
      body: InvoiceConfirmRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<InvoiceResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      InvoiceService.ConfirmInvoicePath,
      'post'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<InvoiceResponse>;
        })
      );
  }

  /**
   * confirm invoice.
   *
   * Confirms a drafted invoice.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `confirmInvoice$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  confirmInvoice(
    params: {
      /**
       * The invoice's id that needs to be updated
       */
      id: number;
      body: InvoiceConfirmRequest;
    },
    context?: HttpContext
  ): Observable<InvoiceResponse> {
    return this.confirmInvoice$Response(params, context).pipe(
      map((r: StrictHttpResponse<InvoiceResponse>): InvoiceResponse => r.body)
    );
  }

  /** Path part for operation `updateInvoiceById()` */
  static readonly UpdateInvoiceByIdPath = '/api/v1/invoices/{id}';

  /**
   * Update invoice.
   *
   * api for update invoice
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateInvoiceById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateInvoiceById$Response(
    params: {
      /**
       * The invoice id that needs to be updated
       */
      id: number;
      body: UpdateInvoiceRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<InvoiceResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      InvoiceService.UpdateInvoiceByIdPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<InvoiceResponse>;
        })
      );
  }

  /**
   * Update invoice.
   *
   * api for update invoice
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateInvoiceById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateInvoiceById(
    params: {
      /**
       * The invoice id that needs to be updated
       */
      id: number;
      body: UpdateInvoiceRequest;
    },
    context?: HttpContext
  ): Observable<InvoiceResponse> {
    return this.updateInvoiceById$Response(params, context).pipe(
      map((r: StrictHttpResponse<InvoiceResponse>): InvoiceResponse => r.body)
    );
  }

  /** Path part for operation `updateConfirmedInvoiceById()` */
  static readonly UpdateConfirmedInvoiceByIdPath =
    '/api/v1/invoices/{id}/confirmed';

  /**
   * Update confirmed invoice.
   *
   * api for update confirmed invoice
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateConfirmedInvoiceById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateConfirmedInvoiceById$Response(
    params: {
      /**
       * The invoice id that needs to be updated
       */
      id: number;
      body: InvoiceUpdateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<InvoiceResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      InvoiceService.UpdateConfirmedInvoiceByIdPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<InvoiceResponse>;
        })
      );
  }

  /**
   * Update confirmed invoice.
   *
   * api for update confirmed invoice
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateConfirmedInvoiceById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateConfirmedInvoiceById(
    params: {
      /**
       * The invoice id that needs to be updated
       */
      id: number;
      body: InvoiceUpdateRequest;
    },
    context?: HttpContext
  ): Observable<InvoiceResponse> {
    return this.updateConfirmedInvoiceById$Response(params, context).pipe(
      map((r: StrictHttpResponse<InvoiceResponse>): InvoiceResponse => r.body)
    );
  }

  /** Path part for operation `createSalesOrderInvoice()` */
  static readonly CreateSalesOrderInvoicePath = '/api/v1/invoices/sales-orders';

  /**
   * Create invoice of sales orders.
   *
   * Create a invoices of sales orders.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createSalesOrderInvoice()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createSalesOrderInvoice$Response(
    params: {
      body: CreateSalesOrderInvoiceRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<InvoiceResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      InvoiceService.CreateSalesOrderInvoicePath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<InvoiceResponse>;
        })
      );
  }

  /**
   * Create invoice of sales orders.
   *
   * Create a invoices of sales orders.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createSalesOrderInvoice$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createSalesOrderInvoice(
    params: {
      body: CreateSalesOrderInvoiceRequest;
    },
    context?: HttpContext
  ): Observable<InvoiceResponse> {
    return this.createSalesOrderInvoice$Response(params, context).pipe(
      map((r: StrictHttpResponse<InvoiceResponse>): InvoiceResponse => r.body)
    );
  }

  /** Path part for operation `getAllZatcaInvoices()` */
  static readonly GetAllZatcaInvoicesPath = '/api/v1/invoices/zatca';

  /**
   * Get list of zatca invoices.
   *
   * Lists all invoices as a page with filters applied.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllZatcaInvoices()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllZatcaInvoices$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields:
       *   - invoiceNumber
       */
      search?: string;

      /**
       * this adds a string search to the following fields:
       *   - client
       */
      clientId?: number;

      /**
       * This allows filtering invoices by their business type
       */
      businessType?: Array<BusinessType>;

      /**
       * This adds a filtering invoice by invoice type
       */
      invoiceType?: string;

      /**
       * This adds a filter to the zatca list
       *  - zatca status
       */
      zatcaStatus?: ZatcaStatus;
      businessCategory?: BusinessCategory;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetZatcaInvoiceResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      InvoiceService.GetAllZatcaInvoicesPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
      rb.query('clientId', params.clientId, {});
      rb.query('businessType', params.businessType, {});
      rb.query('invoiceType', params.invoiceType, {});
      rb.query('zatcaStatus', params.zatcaStatus, {});
      rb.query('businessCategory', params.businessCategory, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetZatcaInvoiceResponse>;
        })
      );
  }

  /**
   * Get list of zatca invoices.
   *
   * Lists all invoices as a page with filters applied.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllZatcaInvoices$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllZatcaInvoices(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields:
       *   - invoiceNumber
       */
      search?: string;

      /**
       * this adds a string search to the following fields:
       *   - client
       */
      clientId?: number;

      /**
       * This allows filtering invoices by their business type
       */
      businessType?: Array<BusinessType>;

      /**
       * This adds a filtering invoice by invoice type
       */
      invoiceType?: string;

      /**
       * This adds a filter to the zatca list
       *  - zatca status
       */
      zatcaStatus?: ZatcaStatus;
      businessCategory?: BusinessCategory;
    },
    context?: HttpContext
  ): Observable<GetZatcaInvoiceResponse> {
    return this.getAllZatcaInvoices$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<GetZatcaInvoiceResponse>
        ): GetZatcaInvoiceResponse => r.body
      )
    );
  }

  /** Path part for operation `markInvoiceManuallyEnteredToEbs()` */
  static readonly MarkInvoiceManuallyEnteredToEbsPath =
    '/api/v1/invoices/{id}/ebs';

  /**
   * mark invoice is manually entered to ebs.
   *
   * api to mark invoice is manually entered to ebs
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `markInvoiceManuallyEnteredToEbs()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  markInvoiceManuallyEnteredToEbs$Response(
    params: {
      /**
       * The invoice id that needs to be updated
       */
      id: number;
      body: InvoiceEbsUpdateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<InvoiceResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      InvoiceService.MarkInvoiceManuallyEnteredToEbsPath,
      'post'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<InvoiceResponse>;
        })
      );
  }

  /**
   * mark invoice is manually entered to ebs.
   *
   * api to mark invoice is manually entered to ebs
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `markInvoiceManuallyEnteredToEbs$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  markInvoiceManuallyEnteredToEbs(
    params: {
      /**
       * The invoice id that needs to be updated
       */
      id: number;
      body: InvoiceEbsUpdateRequest;
    },
    context?: HttpContext
  ): Observable<InvoiceResponse> {
    return this.markInvoiceManuallyEnteredToEbs$Response(params, context).pipe(
      map((r: StrictHttpResponse<InvoiceResponse>): InvoiceResponse => r.body)
    );
  }
}
